import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/shared/lib/api';
import { useTTS } from '@/shared/hooks/useTTS';
import { useIsDesktop } from '@/shared/hooks/useIsDesktop';
import { GiftModal } from '@/features/gamification/GiftModal';
import { trackEvent, captureError } from '@/shared/lib/monitoring';
import { useRpgStore } from '@/stores/rpgStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';
import { AdminEditButton } from './AdminEditButton';
import { renderHighlightedSentence, renderBlankedSentence } from '@/shared/lib/highlight';
import { ComboBadge, RpgProgressBar, EmptySrsView, DailyGoalVictoryWidget, SmartInputFeedback, SmartFeedbackType } from './StudyJuice';
import { playSuccessSound, playErrorSound } from '@/shared/lib/soundEffects';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';
import type { EditWordData } from './InlineWordEdit';

// === Типы ===

interface StudyWord {
  id: number;
  dictionary_word_id?: number | null;
  original_text: string;
  main_translation_text: string;
  srs_level: number;
  mc_en_ru_progress: number;
  mc_ru_en_progress: number;
  input_lives_left: number;
  input_en_ru_done: boolean;
  input_ru_en_done: boolean;
  error_count: number;
  error_streak: number;
  translations: Array<{ translation_text: string }>;
  examples: Array<{ example_sentence: string; example_translation: string }>;
  part_of_speech?: string | null;
  cefr_level?: string | null;
  transcription?: string | null;
  russian_phonetic?: string | null;
  plural_form?: string | null;
}

interface NextWordResponse {
  done: boolean;
  word?: StudyWord;
  direction?: 'original_to_translation' | 'translation_to_original';
  srs_level?: number;
  lives_left?: number;
  is_retry?: boolean;
  is_srs_review?: boolean;
  context_examples?: Array<{ en: string; en_blanked: string; ru: string; ru_blanked: string }>;
  next_review_at?: string | null;
}

interface SubmitResponse {
  verdict: 'correct' | 'alternative_correct' | 'typo' | 'synonym' | 'wrong' | 'empty';
  ai_hint?: string;
  correct_answer: string;
  lives_left: number;
  srs_level: number;
  srs_level_before?: number;
  next_review_hours?: number | null;
  transition_reason?: 'mc_to_srs' | 'srs_level_up' | 'srs_level_down' | 'reset_to_mc' | null;
  is_srs_review?: boolean;
  life_lost: boolean;
  srs_changed: boolean;
  word_reset_to_mc: boolean;
  xp_gained: number;
  loot_drop?: { item: { name: string; description: string; rarity: string; slot: string; flavor_text: string }; rarity: string };
}

type FinishReason = 'manual' | 'session_limit' | 'no_words' | 'load_error';

// Округлённый человекочитаемый текст интервала "через X"
function formatInterval(hours: number): string {
  if (hours <= 0) return 'сейчас';
  if (hours < 24) return `через ${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'}`;
  const days = Math.round(hours / 24);
  if (days < 7) return `через ${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}`;
  if (days < 14) return 'через неделю';
  if (days < 21) return 'через 2 недели';
  if (days < 28) return 'через 3 недели';
  const months = Math.round(days / 30);
  if (months === 1) return 'через месяц';
  if (months < 5) return `через ${months} месяца`;
  return `через ${months} месяцев`;
}

// Размер сессии загружается из /users/settings (mc_batch_size / input_session_size / srs_session_size).
// Значение 0 означает «без ограничения» (∞).
const DEFAULT_SESSION_SIZE = 25;

// === Словари для POS/CEFR тегов ===

const POS_INFO: Record<string, { ru: string }> = {
  noun: { ru: 'существительное' }, verb: { ru: 'глагол' }, adjective: { ru: 'прилагательное' },
  adverb: { ru: 'наречие' }, preposition: { ru: 'предлог' }, conjunction: { ru: 'союз' },
  pronoun: { ru: 'местоимение' }, interjection: { ru: 'междометие' },
};

const POS_COLORS: Record<string, string> = {
  noun: '#378ADD', verb: '#639922', adjective: '#7F77DD', adverb: '#1D9E75',
  preposition: '#888780', conjunction: '#D85A30', pronoun: '#D4537E', interjection: '#EF9F27',
};

const CEFR_INFO: Record<string, { title: string }> = {
  A1: { title: 'Beginner' }, A2: { title: 'Elementary' }, B1: { title: 'Intermediate' },
  B2: { title: 'Upper-Intermediate' }, C1: { title: 'Advanced' }, C2: { title: 'Proficiency' },
};

// === Утилиты ===

const IPA_VOWELS = new Set('aɑæʌɒɔəɛɜɪiɵʊuːeɝoʉ');
const EN_VOWELS = new Set('aeiouyAEIOUY');

function getStressIndex(word: string, ipa: string): number {
  if (!ipa || !word) return -1;
  const stressPos = ipa.indexOf('ˈ');
  if (stressPos === -1) {
    for (let i = 0; i < word.length; i++) {
      if (EN_VOWELS.has(word[i])) return i;
    }
    return -1;
  }
  let ipaVowelIdx = 0;
  for (let i = 0; i < stressPos; i++) {
    if (IPA_VOWELS.has(ipa[i])) ipaVowelIdx++;
  }
  let count = 0;
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) {
      if (count === ipaVowelIdx) return i;
      count++;
    }
  }
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) return i;
  }
  return -1;
}

// Ударение для слов и фраз
function addStressMark(word: string, ipa: string): string {
  const words = word.split(/(\s+|-)/);
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/);
  let ipaIdx = 0;
  return words.map(part => {
    if (/^[\s-]+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    const idx = getStressIndex(part, partIpa);
    if (idx < 0) return part;
    return part.slice(0, idx + 1) + '\u0301' + part.slice(idx + 1);
  }).join('');
}

function addPhoneticStress(phonetic: string, ipa: string): string {
  if (!phonetic || !ipa) return phonetic;
  const cleanPh = phonetic.replace(/\u0301/g, '');
  const RU_VOWELS = new Set('аеёиоуыэюяАЕЁИОУЫЭЮЯ');

  const addSingle = (ph: string, partIpa: string): string => {
    if (!ph) return ph;
    const pos = partIpa.indexOf('ˈ');
    let targetVowelIdx = 0;
    if (pos !== -1) {
      for (let i = 0; i < pos; i++) {
        if (IPA_VOWELS.has(partIpa[i])) targetVowelIdx++;
      }
    }

    const vowelSlots: number[] = [];
    let i = 0;
    while (i < ph.length) {
      const ch = ph[i].toLowerCase();
      if (RU_VOWELS.has(ch)) {
        if (ch === 'у' && i + 1 < ph.length && RU_VOWELS.has(ph[i + 1].toLowerCase())) {
          vowelSlots.push(i + 1);
          i += 2;
          continue;
        } else if ('аоэ'.includes(ch) && i + 1 < ph.length && ph[i + 1].toLowerCase() === 'у') {
          vowelSlots.push(i);
          i += 2;
          continue;
        } else {
          vowelSlots.push(i);
          i += 1;
        }
      } else {
        i += 1;
      }
    }

    if (vowelSlots.length === 0) return ph;
    const stressIdx = targetVowelIdx < vowelSlots.length ? vowelSlots[targetVowelIdx] : vowelSlots[0];
    return ph.slice(0, stressIdx + 1) + '\u0301' + ph.slice(stressIdx + 1);
  };

  const phParts = cleanPh.split(/(\s+)/);
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/).filter(Boolean);
  let ipaIdx = 0;
  return phParts.map(part => {
    if (/^\s+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    return addSingle(part, partIpa);
  }).join('');
}

function simpleMarkdown(text: unknown): string {
  // Защита от не-строки (AI иногда возвращает null/object/число)
  const safe = typeof text === 'string'
    ? text
    : text == null
      ? ''
      : (() => { try { return JSON.stringify(text); } catch { return String(text); } })();
  return safe
    .replace(/### (.+)/g, '<div style="font-size:13px;font-weight:600;margin:10px 0 4px;color:var(--theme-text)">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#c084fc">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(139,92,246,0.15);padding:1px 5px;border-radius:4px;font-size:12px;color:#c084fc">$1</code>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, ' ');
}

// === ExplanationView — рендер новой структурированной подсказки от 2EnBota ===

type ExplanationData = {
  greeting?: unknown;
  meaning?: unknown;
  memory_hook?: unknown;
  examples?: unknown;
  // legacy fallback
  explanation?: unknown;
};

function ExplanationView({ data }: { data: ExplanationData }) {
  const toStr = (v: unknown): string => {
    if (typeof v === 'string') return v.trim();
    if (v == null) return '';
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  const greeting = toStr(data.greeting);
  const meaning = toStr(data.meaning) || toStr(data.explanation);
  const memoryHook = toStr(data.memory_hook);
  const examples = Array.isArray(data.examples) ? data.examples : [];

  // Если вообще ничего нет — fallback
  if (!greeting && !meaning && !memoryHook && examples.length === 0) {
    return <div style={{ color: 'var(--theme-text-muted)' }}>🦊🦝 Не удалось загрузить</div>;
  }

  const sectionStyle = { marginTop: 14 } as const;
  const headerStyle = { fontSize: 11.5, color: 'var(--theme-text-muted)', marginBottom: 6, fontWeight: 700 as const, letterSpacing: 0.5 };
  const bodyStyle = { color: 'var(--theme-text)', lineHeight: 1.6, fontSize: 14.5 };

  return (
    <div>
      {greeting && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
          <span style={{ fontSize: 18, lineHeight: 1.2, flexShrink: 0 }}>🦊🦝</span>
          <div style={{ ...bodyStyle, fontStyle: 'italic', color: 'var(--theme-text)' }} dangerouslySetInnerHTML={{ __html: simpleMarkdown(greeting) }} />
        </div>
      )}

      {meaning && (
        <div style={sectionStyle}>
          <div style={headerStyle}>📖 ЧТО ЗНАЧИТ</div>
          <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: simpleMarkdown(meaning) }} />
        </div>
      )}

      {memoryHook && (
        <div style={sectionStyle}>
          <div style={headerStyle}>🧠 КАК ЗАПОМНИТЬ</div>
          <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: simpleMarkdown(memoryHook) }} />
        </div>
      )}

      {examples.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
          <div style={headerStyle}>💬 ПРИМЕРЫ</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {examples.map((ex: any, i: number) => (
              <div
                key={i}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                }}
              >
                <div style={{ color: 'var(--theme-text)', fontSize: 14.5, fontWeight: 500, lineHeight: 1.4 }}>
                  {toStr(ex?.original || ex?.en || ex?.sentence)}
                </div>
                {ex?.phonetic && (
                  <div style={{ color: '#c084fc', fontSize: 12.5, fontStyle: 'italic', marginTop: 3 }}>
                    {toStr(ex.phonetic)}
                  </div>
                )}
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 13.5, lineHeight: 1.4, marginTop: 4 }}>
                  → {toStr(ex?.translation || ex?.ru)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// === SRS Indicator (кружки) ===

const SRS_COLORS = [
  '#6B7280', // 0 — серый (не в SRS)
  '#3B82F6', // 1
  '#3B82F6', // 2
  '#10B981', // 3
  '#10B981', // 4
  '#F59E0B', // 5
  '#F59E0B', // 6
  '#EF4444', // 7
  '#EF4444', // 8
  '#A855F7', // 9
  '#F59E0B', // 10 — золотой
];

function SrsIndicator({ level }: { level: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 12 }}>
      {Array.from({ length: 10 }).map((_, i) => {
        const filled = i < level;
        const color = filled ? (SRS_COLORS[i + 1] || '#F59E0B') : 'rgba(255,255,255,0.1)';
        return (
          <div
            key={i}
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: color,
              transition: 'background 0.3s',
              border: filled ? 'none' : '1px solid rgba(255,255,255,0.15)',
            }}
          />
        );
      })}
    </div>
  );
}

// === Lives Indicator ===

function LivesIndicator({ lives, maxLives = 3 }: { lives: number; maxLives?: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 12 }}>
      {Array.from({ length: maxLives }).map((_, i) => (
        <span key={i} style={{ fontSize: 20, opacity: i < lives ? 1 : 0.3, transition: 'opacity 0.3s' }}>
          {i < lives ? '❤️' : '🤍'}
        </span>
      ))}
    </div>
  );
}

// === Long Press Hook ===
function useLongPress(callback: () => void, ms = 800) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startLongPress = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault?.();
    timerRef.current = setTimeout(() => {
      callback();
    }, ms);
  }, [callback, ms]);
  const cancelLongPress = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  return { onMouseDown: startLongPress, onMouseUp: cancelLongPress, onMouseLeave: cancelLongPress, onTouchStart: startLongPress, onTouchEnd: cancelLongPress };
}

// === Word Card ===

function WordCard({ word, direction, onWordEdited }: { word: StudyWord; direction: string; onWordEdited?: (updated: EditWordData) => void }) {
  const { speak } = useTTS();
  const [showExplain, setShowExplain] = useState(false);
  const [explanation, setExplanation] = useState<any>(null);
  const [isLoadingExplain, setIsLoadingExplain] = useState(false);

  const isEnDirection = direction === 'original_to_translation';
  const question = isEnDirection ? word.original_text : word.main_translation_text;
  const posInfo = word.part_of_speech ? POS_INFO[word.part_of_speech] : null;
  const cefrInfo = word.cefr_level ? CEFR_INFO[word.cefr_level] : null;
  const posColor = word.part_of_speech ? (POS_COLORS[word.part_of_speech] || 'var(--theme-border)') : 'var(--theme-border)';

  useEffect(() => { setShowExplain(false); setExplanation(null); }, [word.id]);

  const handleTts = () => {
    if (isEnDirection) {
      speak(word.original_text, 'en-US');
      trackEvent('input_v2_tts', { word_id: word.id, word: word.original_text });
    }
  };

  const handleExplain = async () => {
    if (isLoadingExplain) return;
    if (showExplain) { setShowExplain(false); return; }
    trackEvent('input_v2_explain', { word_id: word.id, word: word.original_text });
    setShowExplain(true);
    setIsLoadingExplain(true);
    try {
      // Собираем все валидные переводы слова (главный + альтернативные).
      // В Ввод/Повторении правильным засчитывается ЛЮБОЙ из них, поэтому
      // объяснение должно покрывать все смыслы — multi-meaning ветка на API.
      const allTranslations: string[] = [
        word.main_translation_text,
        ...(word.translations || []).map(t => t.translation_text),
      ].map(t => (t || '').trim()).filter(Boolean);
      const result = await api.post('/ai/explain-word', {
        word: word.original_text,
        translation: word.main_translation_text,
        translations: allTranslations,
      });
      setExplanation(result);
    } catch {
      setExplanation({ meaning: '🦊🦝 Не удалось загрузить объяснение, попробуй ещё раз!' });
    } finally {
      setIsLoadingExplain(false);
    }
  };

  return (
    <>
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <div style={{
          position: 'relative', display: 'flex', background: 'var(--theme-bg-elevated)', borderRadius: 12,
          minHeight: 100, border: '0.5px solid var(--theme-border)', overflow: 'hidden',
        }}>
          <div style={{ width: 4, background: posColor, flexShrink: 0, borderRadius: '12px 0 0 12px' }} />
          <div onClick={handleTts} style={{ flex: 1, padding: '14px 12px', cursor: isEnDirection ? 'pointer' : 'default' }}>
            {/* Строка тегов всегда рендерится, чтобы admin-кнопка ✏️ могла показаться
                даже у слов без POS/CEFR/dict_word_id. AdminEditButton сам вернёт null
                для не-админа, тогда строка будет пустой (визуально незаметно). */}
            {true && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap', minHeight: 22 }}>
                {posInfo && (
                  <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: `${posColor}18`, color: posColor, border: `1px solid ${posColor}40` }}>
                    {word.part_of_speech} — {posInfo.ru}
                  </span>
                )}
                {cefrInfo && (
                  <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'rgba(239,159,39,0.12)', color: '#D4870A', border: '1px solid rgba(239,159,39,0.3)' }}>
                    {word.cefr_level}
                  </span>
                )}
                {word.plural_form && isEnDirection && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: 'rgba(56, 189, 248, 0.12)',
                      color: '#0284c7',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      userSelect: 'none',
                    }}
                  >
                    мн. ч.: {word.plural_form}
                  </span>
                )}
                <AdminEditButton
                  dictionaryWordId={word.dictionary_word_id}
                  userWordId={word.id}
                  onSaved={onWordEdited}
                  inline
                />
              </div>
            )}
            <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--theme-text)', lineHeight: 1.2 }}>
              {isEnDirection && word.transcription ? addStressMark(question, word.transcription) : question}
            </div>
            {isEnDirection && (word.transcription || word.russian_phonetic) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 13 }}>
                {word.transcription && <span style={{ color: 'var(--theme-text-muted)' }}>{word.transcription}</span>}
                {word.russian_phonetic && (
                  <span style={{ color: 'var(--theme-text-secondary, #666)' }}>
                    {addPhoneticStress(word.russian_phonetic, word.transcription || '')}
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={{ width: 78, display: 'flex', flexDirection: 'column', borderLeft: '0.5px solid var(--theme-border)', flexShrink: 0 }}>
            <button onClick={(e) => { e.stopPropagation(); handleTts(); }} disabled={!isEnDirection}
              style={{ flex: 1, background: 'rgba(55,138,221,0.08)', border: 'none', borderBottom: '0.5px solid var(--theme-border)', borderRadius: '0 12px 0 0', cursor: !isEnDirection ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: !isEnDirection ? 0.3 : 1 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#378ADD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
              </svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleExplain(); }} disabled={isLoadingExplain}
              style={{ flex: 1, background: showExplain ? 'rgba(239,159,39,0.15)' : 'rgba(239,159,39,0.08)', border: 'none', borderRadius: '0 0 12px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isLoadingExplain ? 0.5 : 1 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4870A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showExplain && (
        <div style={{ marginBottom: 12, padding: '14px 16px', background: 'var(--theme-bg-elevated)', borderRadius: 14, borderLeft: '4px solid', borderImage: 'linear-gradient(to bottom, #2196F3, #8B5CF6, #EC4899) 1', fontSize: 13, maxHeight: 460, overflowY: 'auto' }}>
          {isLoadingExplain ? (
            <div style={{ color: 'var(--theme-text-muted)', textAlign: 'center', padding: '16px 0' }}>🦊🦝 Готовим объяснение...</div>
          ) : explanation ? (
            <ExplanationView data={explanation} />
          ) : (
            <div style={{ color: 'var(--theme-text-muted)' }}>🦊🦝 Не удалось загрузить</div>
          )}
        </div>
      )}
    </>
  );
}

// === Context Example ===

// === Иконки для ExampleBlock ===
const ExTranslateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 8l6 6" /><path d="M4 14l6-6 2-3" /><path d="M2 5h12" /><path d="M7 2h1" />
    <path d="m22 22-5-10-5 10" /><path d="M14 18h6" />
  </svg>
);
const ExChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);
const ExSparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
    <path d="M19 15l.6 2.4L22 18l-2.4.6L19 21l-.6-2.4L16 18l2.4-.6L19 15z" />
  </svg>
);

const GENERATING_PHRASES: Array<{ icon: string; text: string }> = [
  { icon: '✨', text: '2EnBota придумывает новый пример...' },
  { icon: '✨', text: 'Колдуем над новым предложением...' },
  { icon: '🦝', text: 'Сочиняем пример из реальной жизни...' },
  { icon: '🦊', text: 'Подбираем классный пример...' },
  { icon: '✨', text: 'Создаём предложение с этим словом...' },
  { icon: '🦝', text: 'Еноты уже пишут новый пример...' },
  { icon: '🦊', text: 'Генерируем ваше предложение...' },
];

function ExampleBlock({ examples, wordOriginal, wordTranslation, direction, isAnswered }: {
  examples: Array<{ en: string; en_blanked: string; ru: string; ru_blanked: string }>;
  wordOriginal: string;
  wordTranslation: string;
  direction?: string;
  isAnswered?: boolean;
}) {
  const [showTranslation, setShowTranslation] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState(GENERATING_PHRASES[0]);
  const [exIndex, setExIndex] = useState(0);
  const [extraExamples, setExtraExamples] = useState<Array<{ en?: string; en_blanked: string; ru: string }>>([]);
  const [history, setHistory] = useState<string[]>([]);

  const allExamples = [
    ...examples.map(e => ({ en: e.en, en_blanked: e.en_blanked, ru: e.ru })),
    ...extraExamples,
  ];
  const current = allExamples[exIndex] || allExamples[0];

  useEffect(() => {
    setShowTranslation(false);
    setExIndex(0);
    setExtraExamples([]);
    setHistory(examples.map(e => e.en_blanked));
  }, [examples]);

  if (!examples || examples.length === 0) return null;

  // Если направление EN -> RU или уже дан ответ, показываем слово целиком с жирной цветной подсветкой
  const isEnDirection = direction === 'original_to_translation';
  const shouldShowFull = isEnDirection || isAnswered;

  const handleNext = () => {
    if (allExamples.length <= 1) return;
    setIsSwapping(true);
    setShowTranslation(false);
    setTimeout(() => {
      setExIndex(prev => (prev + 1) % allExamples.length);
      setIsSwapping(false);
    }, 280);
  };

  const handleGenerate = async () => {
    if (isGenerating) return;
    setLoadingPhrase(GENERATING_PHRASES[Math.floor(Math.random() * GENERATING_PHRASES.length)]);
    setIsGenerating(true);
    setShowTranslation(false);
    try {
      const result = await api.post<{ examples: Array<{ sentence: string; sentence_blanked: string; translation: string }> }>(
        '/ai/generate-example',
        { word: wordOriginal, translation: wordTranslation, previous_examples: history },
      );
      const ex = result.examples?.[0];
      if (ex) {
        const newItem = { en: ex.sentence, en_blanked: ex.sentence_blanked, ru: ex.translation };
        setIsSwapping(true);
        setTimeout(() => {
          setExtraExamples(prev => {
            const updated = [...prev, newItem];
            setExIndex(examples.length + updated.length - 1);
            return updated;
          });
          setHistory(prev => [...prev, ex.sentence_blanked]);
          setIsSwapping(false);
        }, 200);
      }
    } catch {
      // ignore
    } finally {
      setIsGenerating(false);
    }
  };

  const renderSentenceContent = () => {
    if (isGenerating) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c084fc', fontSize: 13.5, fontStyle: 'normal' }}>
          <span style={{ display: 'inline-block', animation: 'spin 1.2s linear infinite' }}>{loadingPhrase.icon}</span>
          <span style={{ fontWeight: 500 }}>{loadingPhrase.text}</span>
        </div>
      );
    }

    if (shouldShowFull) {
      let sentence = current?.en || '';
      if (!sentence || /_{3,}/.test(sentence)) {
        sentence = (current?.en_blanked || '').replace(/_{3,}/g, wordOriginal);
      }
      if (!sentence) sentence = current?.en_blanked || '';
      return renderHighlightedSentence(sentence, wordOriginal);
    }

    const sentenceToMask = current?.en_blanked || current?.en || '';
    return renderBlankedSentence(sentenceToMask, wordOriginal);
  };

  return (
    <div style={{
      width: '100%', maxWidth: 400, display: 'flex', borderRadius: 14, overflow: 'hidden',
      background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
      marginTop: 10, marginBottom: 12,
    }}>
      <div style={{ width: 4, flexShrink: 0, background: 'linear-gradient(to bottom, #2196F3, #8B5CF6, #EC4899)' }} />
      <div style={{ flex: 1, padding: '14px 18px' }}>
        {/* Предложение с подсветкой или пропуском */}
        <div style={{
          fontSize: 15, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)',
          lineHeight: 1.6, minHeight: 24,
          transition: 'opacity 0.25s, transform 0.25s',
          opacity: isSwapping ? 0 : 1,
          transform: isSwapping ? 'translateY(-6px)' : 'translateY(0)',
        }}>
          {renderSentenceContent()}
        </div>

        {/* Перевод (скрытый) */}
        <div style={{
          overflow: 'hidden',
          maxHeight: showTranslation && !isSwapping ? 50 : 0,
          opacity: showTranslation && !isSwapping ? 1 : 0,
          transition: 'max-height 0.35s cubic-bezier(0.16,1,0.3,1), opacity 0.3s ease, margin 0.3s ease',
          marginTop: showTranslation && !isSwapping ? 10 : 0,
        }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 10 }} />
          <div style={{ fontSize: 13.5, color: '#c084fc' }}>{current?.ru}</div>
        </div>

        {/* Кнопки */}
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', marginTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: 12,
        }}>
          <button onClick={() => setShowTranslation(v => !v)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '9px 0', borderRadius: 10,
            border: showTranslation ? '1px solid rgba(139,92,246,0.35)' : '1px solid rgba(255,255,255,0.07)',
            background: showTranslation ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.03)',
            color: showTranslation ? '#c084fc' : 'rgba(255,255,255,0.4)',
            fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s ease',
          }}>
            <ExTranslateIcon />
            {showTranslation ? 'Скрыть' : 'Перевод'}
          </button>

          <button onClick={handleNext} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '9px 0', borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)',
            color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}>
            <ExChevronIcon />
            Ещё
          </button>

          <button onClick={handleGenerate} disabled={isGenerating} style={{
            width: 38, height: 38, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10,
            border: '1px solid rgba(236,72,153,0.25)',
            background: isGenerating ? 'rgba(236,72,153,0.15)' : 'rgba(236,72,153,0.08)',
            color: 'rgba(236,72,153,0.75)', cursor: isGenerating ? 'wait' : 'pointer',
            transition: 'all 0.2s ease', opacity: isGenerating ? 0.6 : 1,
          }}>
            <ExSparkleIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// === Session Stats (экран при выходе) ===

function SessionStats({ stats, maxCombo = 0, finishReason, onContinue, onExit, srsOnly = false, nextReviewAt }: {
  stats: { wordsStudied: number; correctFirstTry: number; errors: number; xp: number };
  maxCombo?: number;
  finishReason: FinishReason;
  onContinue: () => void;
  onExit: () => void;
  srsOnly?: boolean;
  nextReviewAt?: string | null;
}) {
  const isNoWords = finishReason === 'no_words';

  if (srsOnly && isNoWords) {
    return (
      <EmptySrsView
        nextReviewAt={nextReviewAt}
        wordsStudied={stats.wordsStudied}
        xpGained={stats.xp}
        onExit={onExit}
      />
    );
  }

  const accuracy = stats.wordsStudied > 0
    ? Math.round((stats.correctFirstTry / stats.wordsStudied) * 100) : 0;
  const isLoadError = finishReason === 'load_error';
  const title = isNoWords
    ? 'Слова в этом режиме закончились'
    : isLoadError
      ? 'Не удалось загрузить следующее слово'
      : 'Сессия завершена!';
  const description = isNoWords
    ? 'Пока больше нет доступных карточек. Можно выйти и выбрать другой режим.'
    : isLoadError
      ? 'Проверь соединение и попробуй продолжить ещё раз.'
      : null;
  const continueLabel = finishReason === 'session_limit'
    ? 'Продолжить новую сессию'
    : isLoadError
      ? 'Попробовать ещё раз'
      : 'Продолжить учёбу';

  return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>
        {isNoWords ? '🏁' : isLoadError ? '⚠️' : accuracy >= 80 ? '🎉' : accuracy >= 50 ? '💪' : '📚'}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 20 }}>
        {title}
      </h2>
      {description && (
        <p style={{ color: 'var(--theme-text-secondary)', margin: '-12px auto 20px', maxWidth: 320, lineHeight: 1.5 }}>
          {description}
        </p>
      )}

      {/* Виджет дневной нормы (спидометр) */}
      <DailyGoalVictoryWidget cardsAnsweredInSession={stats.wordsStudied} />

      {/* RPG XP Bar */}
      <RpgProgressBar xpGained={stats.xp} />

      <div style={{ display: 'grid', gap: 12, maxWidth: 320, margin: '0 auto 32px' }}>
        {[
          { icon: '📊', label: 'Слов пройдено', value: String(stats.wordsStudied) },
          { icon: '✅', label: 'С первой попытки', value: String(stats.correctFirstTry) },
          { icon: '❌', label: 'Ошибок', value: String(stats.errors) },
          { icon: '🎯', label: 'Точность', value: `${accuracy}%` },
          ...(maxCombo >= 2 ? [{ icon: '🔥', label: 'Лучший комбо', value: `x${maxCombo}`, color: '#f59e0b' }] : []),
          { icon: '⭐', label: 'XP', value: `+${stats.xp}` },
        ].map((row, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: 'var(--theme-bg-elevated)', borderRadius: 10,
          }}>
            <span style={{ fontSize: 14, color: 'var(--theme-text-secondary)' }}>{row.icon} {row.label}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: (row as any).color || 'var(--theme-text)' }}>{row.value}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320, margin: '0 auto' }}>
        {!isNoWords && (
          <button onClick={onContinue} style={{
            padding: '14px', background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
            border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
          }}>
            {continueLabel}
          </button>
        )}
        <button onClick={onExit} style={{
          padding: '14px', background: 'var(--theme-bg-elevated)', color: 'var(--theme-text-secondary)',
          border: '1px solid var(--theme-border)', borderRadius: 12, fontSize: 16, cursor: 'pointer',
        }}>
          Выйти
        </button>
      </div>
    </div>
  );
}

// === Главный компонент ===

export function InputModeView({ onExit, srsOnly = false }: { onExit: () => void; srsOnly?: boolean }) {
  const isDesktop = useIsDesktop();
  const { speak } = useTTS();
  const { autoAdvance } = useSettingsStore();
  const { notify, impact } = useHapticFeedback();

  // Текущее слово
  const [currentData, setCurrentData] = useState<NextWordResponse | null>(null);
  const [prefetchedData, setPrefetchedData] = useState<NextWordResponse | null>(null);
  const prefetchRef = useRef<Promise<NextWordResponse> | null>(null);

  // Состояние ввода
  const [userInput, setUserInput] = useState('');
  const [cardState, setCardState] = useState<'input' | 'checking' | 'feedback' | 'correct'>('input');
  const [lastVerdict, setLastVerdict] = useState<SubmitResponse | null>(null);
  const [hadWrongThisCard, setHadWrongThisCard] = useState(false);
  const hadWrongRef = useRef(false);       // были WRONG (для жизней)
  const hadAnyMissRef = useRef(false);     // были WRONG или TYPO (для SRS)
  const [localLives, setLocalLives] = useState(3);
  const [localSrsLevel, setLocalSrsLevel] = useState(0);
  const [lastSubmittedInput, setLastSubmittedInput] = useState('');

  // Плашка уведомления о переходе SRS-состояния
  const [transitionToast, setTransitionToast] = useState<string | null>(null);

  // Размер сессии из настроек пользователя (0 = без ограничения)
  const [sessionCap, setSessionCap] = useState<number>(DEFAULT_SESSION_SIZE);

  const [pendingAdvance, setPendingAdvance] = useState(false);

  // Статистика сессии
  const [stats, setStats] = useState({ wordsStudied: 0, correctFirstTry: 0, errors: 0, xp: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [finishReason, setFinishReason] = useState<FinishReason>('manual');
  const [isLoading, setIsLoading] = useState(true);
  const [nextReviewAt, setNextReviewAt] = useState<string | null>(null);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [isCardShaking, setIsCardShaking] = useState(false);

  // Loot
  const [showGift, setShowGift] = useState(false);
  const [lootDrop, setLootDrop] = useState<SubmitResponse['loot_drop'] | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);

  const finishSession = useCallback((reason: FinishReason) => {
    setFinishReason(reason);
    setIsFinished(true);
  }, []);

  // Загрузка следующего слова (передаём last_word_id чтобы не дублировать)
  const fetchNextWord = useCallback(async (lastWordId?: number): Promise<NextWordResponse> => {
    const params = new URLSearchParams();
    if (lastWordId) params.set('last_word_id', String(lastWordId));
    if (srsOnly) params.set('srs_only', 'true');
    const qs = params.toString();
    const url = qs ? `/study/input/next-word?${qs}` : '/study/input/next-word';
    const data = await api.get<NextWordResponse>(url);
    return data;
  }, [srsOnly]);

  // Prefetch — передаём текущий word id чтобы не получить то же слово
  const startPrefetch = useCallback((currentWordId?: number) => {
    prefetchRef.current = fetchNextWord(currentWordId).catch(err => {
      captureError(err, { context: 'InputMode.prefetch' });
      return { done: true } as NextWordResponse;
    });
  }, [fetchNextWord]);

  // Загружаем размер сессии из настроек пользователя при старте
  useEffect(() => {
    let cancelled = false;
    api.get<{ input_session_size?: number; srs_session_size?: number }>('/users/settings')
      .then(s => {
        if (cancelled || !s) return;
        const raw = srsOnly ? s.srs_session_size : s.input_session_size;
        if (typeof raw === 'number') setSessionCap(raw);
      })
      .catch(() => { /* без настроек оставляем default */ });
    return () => { cancelled = true; };
  }, [srsOnly]);

  // Инициализация — грузим первое слово + prefetch
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchNextWord().then(data => {
      if (cancelled) return;
      if (data.done) {
        if (data.next_review_at) setNextReviewAt(data.next_review_at);
        trackEvent('input_v2_no_words', {});
        finishSession('no_words');
      } else {
        setCurrentData(data);
        setLocalLives(data.lives_left ?? 3);
        setLocalSrsLevel(data.srs_level ?? 0);
        trackEvent('input_v2_session_start', {
          first_word_id: data.word?.id,
          first_word: data.word?.original_text,
          direction: data.direction,
          srs_level: data.srs_level,
        });
        startPrefetch(data.word?.id);
      }
      setIsLoading(false);
    }).catch(err => {
      if (cancelled) return;
      captureError(err, { context: 'InputMode.init' });
      setIsLoading(false);
      finishSession('load_error');
    });
    return () => { cancelled = true; };
  }, [fetchNextWord, finishSession, startPrefetch]);

  // Фокусируем input + скроллим кнопку в видимую зону при открытии клавиатуры
  useEffect(() => {
    if (cardState === 'input' || cardState === 'feedback') {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);

      // Когда клавиатура открывается, visualViewport уменьшается — скроллим кнопку
      const scrollToButton = () => {
        setTimeout(() => {
          scrollAnchorRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }, 100);
      };

      window.visualViewport?.addEventListener('resize', scrollToButton);
      return () => {
        clearTimeout(timer);
        window.visualViewport?.removeEventListener('resize', scrollToButton);
      };
    }
  }, [cardState, currentData?.word?.id]);

  // Переход к следующему слову
  const advanceToNext = useCallback(async (ignoreSessionCap = false) => {
    // Закрываем плашку перехода перед следующим словом
    setTransitionToast(null);

    // Сессия ограничена sessionCap карточками (0 = без ограничения)
    if (!ignoreSessionCap && sessionCap > 0 && stats.wordsStudied >= sessionCap) {
      finishSession('session_limit');
      return;
    }

    // Пробуем взять prefetched
    let nextData: NextWordResponse | null = null;
    if (prefetchRef.current) {
      try {
        nextData = await prefetchRef.current;
      } catch {
        nextData = null;
      }
      prefetchRef.current = null;
    }

    // Текущее слово — чтобы не получить его же
    const lastId = currentData?.word?.id;

    if (!nextData || (!nextData.done && nextData.word?.id === lastId)) {
      // Prefetch вернул то же слово или не было — запрашиваем новое
      try {
        nextData = await fetchNextWord(lastId);
      } catch (err) {
        captureError(err, { context: 'InputMode.advance' });
        finishSession('load_error');
        return;
      }
    }

    if (nextData.done) {
      if (nextData.next_review_at) setNextReviewAt(nextData.next_review_at);
      finishSession('no_words');
      return;
    }

    setCurrentData(nextData);
    setLocalLives(nextData.lives_left ?? 3);
    setLocalSrsLevel(nextData.srs_level ?? 0);
    setUserInput('');
    setCardState('input');
    setLastVerdict(null);
    setHadWrongThisCard(false);
    hadWrongRef.current = false;
    hadAnyMissRef.current = false;

    trackEvent('input_v2_card_shown', {
      word_id: nextData.word?.id,
      word: nextData.word?.original_text,
      direction: nextData.direction,
      srs_level: nextData.srs_level,
      lives_left: nextData.lives_left,
      is_retry: nextData.is_retry,
    });

    // Prefetch следующее, исключая новое текущее
    startPrefetch(nextData.word?.id);
  }, [fetchNextWord, finishSession, startPrefetch, currentData, stats.wordsStudied, sessionCap]);

  // Отправка ответа
  const handleSubmit = useCallback(async () => {
    if (!currentData?.word || cardState === 'checking' || cardState === 'correct') return;

    const trimmed = userInput.trim();
    setLastSubmittedInput(trimmed);
    setCardState('checking');

    try {
      const result = await api.post<SubmitResponse>('/study/input/submit', {
        word_id: currentData.word.id,
        direction: currentData.direction,
        user_input: trimmed,
        is_first_wrong_this_card: !hadWrongRef.current,
        is_clean_attempt: !hadAnyMissRef.current,
        is_srs_review: srsOnly,
      });

      setLastVerdict(result);

      const prevLives = localLives;
      const prevSrs = localSrsLevel;
      setLocalLives(result.lives_left);
      setLocalSrsLevel(result.srs_level);

      // === PostHog: HP изменение ===
      if (result.lives_left < prevLives) {
        trackEvent('input_v2_hp_lost', {
          word_id: currentData.word.id,
          word: currentData.word.original_text,
          hp_before: prevLives,
          hp_after: result.lives_left,
        });
      } else if (result.lives_left > prevLives) {
        trackEvent('input_v2_hp_restored', {
          word_id: currentData.word.id,
          word: currentData.word.original_text,
          hp_before: prevLives,
          hp_after: result.lives_left,
        });
      }

      // === PostHog: SRS изменение ===
      if (result.srs_changed) {
        if (result.srs_level > prevSrs) {
          trackEvent('input_v2_srs_up', {
            word_id: currentData.word.id,
            word: currentData.word.original_text,
            srs_before: prevSrs,
            srs_after: result.srs_level,
          });
        } else if (result.srs_level < prevSrs) {
          trackEvent('input_v2_srs_down', {
            word_id: currentData.word.id,
            word: currentData.word.original_text,
            srs_before: prevSrs,
            srs_after: result.srs_level,
          });
        }
      }

      const isCorrect = result.verdict === 'correct' || result.verdict === 'alternative_correct';
      const isTypo = result.verdict === 'typo' || result.verdict === 'synonym';
      const isFirstTry = !hadWrongRef.current;

      // === PostHog: основное событие ответа ===
      trackEvent('input_v2_answer', {
        word_id: currentData.word.id,
        word: currentData.word.original_text,
        direction: currentData.direction,
        verdict: result.verdict,
        user_input: trimmed || '(пусто)',
        is_first_try: isFirstTry,
        is_retry_word: currentData.is_retry,
        life_lost: result.life_lost,
        lives_left: result.lives_left,
        srs_level: result.srs_level,
        srs_changed: result.srs_changed,
        word_reset_to_mc: result.word_reset_to_mc,
        xp_gained: result.xp_gained,
        had_wrong_before: hadWrongRef.current,
      });

      if (isCorrect) {
        if (isFirstTry) {
          notify('success');
        } else {
          impact('light');
        }
        setCombo(c => {
          const next = c + 1;
          setMaxCombo(m => Math.max(m, next));
          playSuccessSound(next);
          if (next >= 2) impact(next >= 10 ? 'heavy' : next >= 5 ? 'medium' : 'light');
          return next;
        });

        // === PostHog: правильный ответ ===
        trackEvent('input_v2_correct', {
          word_id: currentData.word.id,
          word: currentData.word.original_text,
          direction: currentData.direction,
          is_first_try: isFirstTry,
          srs_level: result.srs_level,
          srs_changed: result.srs_changed,
          xp_gained: result.xp_gained,
          verdict: result.verdict, // 'correct' vs 'alternative_correct'
        });

        setCardState('correct');
        setStats(s => ({
          ...s,
          wordsStudied: s.wordsStudied + 1,
          correctFirstTry: s.correctFirstTry + (isFirstTry ? 1 : 0),
          xp: s.xp + result.xp_gained,
        }));

        // Плашка для перехода SRS-состояния
        if (result.transition_reason === 'mc_to_srs') {
          const interval = result.next_review_hours != null ? formatInterval(result.next_review_hours) : '';
          setTransitionToast(`🎉 Слово отправлено в Повторение! Вернётся ${interval}`);
          setTimeout(() => impact('heavy'), 180);
        } else if (result.transition_reason === 'srs_level_up' && result.next_review_hours != null && isFirstTry) {
          setTransitionToast(`⭐ Уровень ${result.srs_level_before} → ${result.srs_level}, вернётся ${formatInterval(result.next_review_hours)}`);
          setTimeout(() => impact('medium'), 180);
        }

        if (result.loot_drop) {
          setLootDrop(result.loot_drop);
          trackEvent('input_v2_loot_drop', {
            word_id: currentData.word.id,
            rarity: result.loot_drop.rarity,
            item_name: result.loot_drop.item.name,
          });
          setTimeout(() => notify('success'), 350);
          setTimeout(() => setShowGift(true), 500);
        }

        // В режиме Повторения держим плашку перехода подольше (2.5с),
        // в обычном Вводе быстрее — там переходных плашек нет.
        if (autoAdvance) {
          setTimeout(() => advanceToNext(), srsOnly ? 2500 : 900);
        } else {
          setPendingAdvance(true);
        }
      } else if (isTypo) {
        notify('warning');
        setCombo(0);
        setIsCardShaking(true);
        playErrorSound();
        setTimeout(() => setIsCardShaking(false), 500);

        // Typo / synonym маркирует карточку как "не чистая" (SRS не повысится).
        // Также защищает от халва/потери жизни на последующих wrong: правильный ответ
        // уже показан на экране, дальше пользователь обязан ввести правильно, но
        // штраф за это уже не накладывается (как будто wrong уже был).
        hadAnyMissRef.current = true;
        hadWrongRef.current = true;

        if (result.verdict === 'synonym') {
          // === PostHog: synonym ===
          trackEvent('input_v2_synonym', {
            word_id: currentData.word.id,
            word: currentData.word.original_text,
            direction: currentData.direction,
            user_input: trimmed,
            ai_hint: result.ai_hint,
          });
        } else {
          // === PostHog: опечатка ===
          trackEvent('input_v2_typo', {
            word_id: currentData.word.id,
            word: currentData.word.original_text,
            direction: currentData.direction,
            user_input: trimmed,
            ai_hint: result.ai_hint,
          });
        }

        setCardState('feedback');
        setUserInput('');
      } else {
        notify('error');
        setCombo(0);
        setIsCardShaking(true);
        playErrorSound();
        setTimeout(() => setIsCardShaking(false), 500);

        // wrong / empty
        // === PostHog: ошибка ===
        trackEvent('input_v2_wrong', {
          word_id: currentData.word.id,
          word: currentData.word.original_text,
          direction: currentData.direction,
          user_input: trimmed || '(пусто)',
          verdict: result.verdict,
          is_first_wrong: isFirstTry,
          life_lost: result.life_lost,
          lives_left: result.lives_left,
          srs_level: result.srs_level,
          srs_changed: result.srs_changed,
          word_reset_to_mc: result.word_reset_to_mc,
        });

        if (!hadWrongRef.current) {
          hadWrongRef.current = true;
          hadAnyMissRef.current = true;
          setHadWrongThisCard(true);
          setStats(s => ({ ...s, errors: s.errors + 1 }));
        }

        if (result.word_reset_to_mc) {
          // === PostHog: слово вернулось в MC ===
          trackEvent('input_v2_word_reset_to_mc', {
            word_id: currentData.word.id,
            word: currentData.word.original_text,
            srs_level_was: localSrsLevel,
          });

          setCardState('feedback');
          setStats(s => ({ ...s, wordsStudied: s.wordsStudied + 1 }));
          if (autoAdvance) {
            setTimeout(() => advanceToNext(), 2500);
          } else {
            setPendingAdvance(true);
          }
        } else if (srsOnly && result.is_srs_review) {
          // Режим Повторения: ошибка применяет halve один раз (на is_first_wrong_this_card).
          // Дальше карточка остаётся открытой — пользователь обязан ввести правильно перед
          // переходом к следующему слову. Повторные ошибки больше не меняют SRS.
          setCardState('feedback');
          setUserInput('');

          if (result.transition_reason === 'srs_level_down' && result.next_review_hours != null) {
            setTransitionToast(`📉 Уровень ${result.srs_level_before} → ${result.srs_level}, вернётся ${formatInterval(result.next_review_hours)}`);
          }
          // wordsStudied и advanceToNext не делаем — это произойдёт после правильного ввода.
        } else {
          setCardState('feedback');
          setUserInput('');
        }
      }
    } catch (err: any) {
      captureError(err, { context: 'InputMode.submit' });
      // При ошибке просто возвращаем в ввод
      setCardState('input');
    }
  }, [currentData, userInput, cardState, hadWrongThisCard, advanceToNext, impact, notify]);

  // Ручной переход по кнопке "Далее"
  const handleManualAdvance = useCallback(() => {
    trackEvent('input_v2_manual_advance', {
      word_id: currentData?.word?.id,
      word: currentData?.word?.original_text,
    });
    setPendingAdvance(false);
    advanceToNext();
  }, [advanceToNext, currentData]);

  // Hotkey: Enter для "Далее" при autoAdvance=false
  useEffect(() => {
    if (!pendingAdvance) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleManualAdvance();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pendingAdvance, handleManualAdvance]);

  // Hotkey: Shift+2 = TTS
  useEffect(() => {
    if (!isDesktop || !currentData?.word) return;
    const handler = (e: KeyboardEvent) => {
      if (e.shiftKey && (e.key === '2' || e.code === 'Digit2')) {
        e.preventDefault();
        speak(currentData.word!.original_text, 'en-US');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop, speak, currentData?.word]);

  // Выход — всегда показываем статистику сессии (даже если 0 карточек)
  const handleExit = useCallback(() => {
    const accuracy = stats.wordsStudied > 0
      ? Math.round((stats.correctFirstTry / stats.wordsStudied) * 100) : 0;
    trackEvent('input_v2_session_end', {
      ...stats,
      accuracy,
      auto_advance: autoAdvance,
    });
    useRpgStore.getState().load();
    finishSession('manual');
  }, [stats, autoAdvance, finishSession]);

  const handleStatsContinue = useCallback(() => {
    setIsFinished(false);
    if (finishReason === 'session_limit' || finishReason === 'load_error') {
      setStats({ wordsStudied: 0, correctFirstTry: 0, errors: 0, xp: 0 });
      setPendingAdvance(false);
      void advanceToNext(true);
    }
  }, [advanceToNext, finishReason]);

  // Перехват свайпа назад и BackButton Telegram
  useBackHandler(() => {
    if (isFinished) {
      onExit();
    } else {
      // Если в процессе ввода слова — ставим на паузу и показываем экран SessionStats с возможностью вернуться
      handleExit();
    }
  }, true, 10);

  // === Рендер ===

  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
        Загрузка...
      </div>
    );
  }

  // Экран статистики
  if (isFinished) {
    // Всегда показываем SessionStats — даже если ещё не ответили ни на одно слово.
    // Продолжить = вернуться к текущей карточке, а не грузить следующую.
    return (
      <SessionStats
        stats={stats}
        maxCombo={maxCombo}
        finishReason={finishReason}
        onContinue={handleStatsContinue}
        onExit={onExit}
        srsOnly={srsOnly}
        nextReviewAt={nextReviewAt}
      />
    );
  }

  if (!currentData?.word) return null;

  const word = currentData.word;
  const direction = currentData.direction!;
  const correctAnswer = direction === 'original_to_translation' ? word.main_translation_text : word.original_text;
  const isWrongFeedback = lastVerdict && (lastVerdict.verdict === 'wrong' || lastVerdict.verdict === 'empty');
  const isTypoFeedback = lastVerdict?.verdict === 'typo';
  const isSynonymFeedback = lastVerdict?.verdict === 'synonym';
  const isWrongLanguage = isTypoFeedback && (lastVerdict?.ai_hint || '').includes('том же языке');

  // Все русские формы слова (main + альтернативные). Альтернативы — это другие
  // значения того же английского слова (например, patient = пациент / терпеливый).
  const russianForms = Array.from(new Set([
    word.main_translation_text,
    ...(word.translations?.map(t => t.translation_text) || []),
  ].filter(Boolean))) as string[];
  const norm = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е');
  const userNorm = norm(lastSubmittedInput || '');
  // Что пользователь уже видел/вводил — исключаем из «других значений»:
  // en→ru: пользователь сам набрал один из переводов;
  // ru→en: ему был показан main_translation_text как промпт.
  const correctNorm = norm(correctAnswer || word.main_translation_text || '');
  const seenNorm = direction === 'original_to_translation'
    ? userNorm
    : norm(word.main_translation_text || '');
  const otherTranslations = russianForms.filter(t => {
    const nt = norm(t);
    return nt !== seenNorm && nt !== correctNorm && nt !== userNorm;
  });
  const isResetToMc = lastVerdict?.word_reset_to_mc;

  return (
    <div>
      {/* Плашка перехода SRS-состояния */}
      {transitionToast && (
        <div
          onClick={() => setTransitionToast(null)}
          style={{
            position: 'fixed',
            top: 80,
            left: 0,
            right: 0,
            textAlign: 'center',
            zIndex: 100,
            cursor: 'pointer',
          }}
        >
          <span style={{
            display: 'inline-block',
            background: 'var(--theme-bg-elevated)',
            border: '1px solid var(--theme-primary)',
            borderRadius: 20,
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--theme-primary)',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100vw - 32px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            animation: 'fadeIn 0.3s ease',
            boxShadow: 'var(--theme-shadow)',
          }}>
            {transitionToast}
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 16px 0', marginTop: 4,
      }}>
        <button onClick={handleExit} style={{
          background: 'none', border: 'none', color: 'var(--theme-text-secondary)',
          fontSize: 20, cursor: 'pointer', padding: 4,
        }}>
          ✕
        </button>
        <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', textAlign: 'center' }}>
          {srsOnly ? (
            <div style={{ fontWeight: 600, color: 'var(--theme-primary)', marginBottom: 2 }}>
              Повторение · {sessionCap > 0
                ? `${Math.min(stats.wordsStudied + 1, sessionCap)} / ${sessionCap}`
                : `${stats.wordsStudied + 1}`}
            </div>
          ) : (
            sessionCap > 0 && (
              <div style={{ fontWeight: 600, color: 'var(--theme-primary)', marginBottom: 2 }}>
                Ввод · {Math.min(stats.wordsStudied + 1, sessionCap)} / {sessionCap}
              </div>
            )
          )}
          <div>
            {direction === 'original_to_translation' ? 'EN → RU' : 'RU → EN'}
            {currentData.is_retry && (
              <span style={{ marginLeft: 8, color: 'var(--theme-warning, #F59E0B)' }}>🔄 повтор</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--theme-success)', fontWeight: 600 }}>{stats.correctFirstTry} ✓</span>
          <span style={{ fontSize: 13, color: 'var(--theme-error)', fontWeight: 600 }}>{stats.errors} ✗</span>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }} className={isCardShaking ? 'card-shake' : ''}>
        {/* SRS Indicator */}
        <SrsIndicator level={localSrsLevel} />

        {/* Lives — скрыты в режиме Повторения (там нет жизней, только halve уровня) */}
        {!srsOnly && <LivesIndicator lives={localLives} />}

        {/* Combo Badge */}
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: 28, margin: '6px 0' }}>
          <ComboBadge combo={combo} />
        </div>

        {/* Word Card */}
        <WordCard
          word={word}
          direction={direction}
          onWordEdited={(updated) => {
            // Оптимистично обновляем переводы текущего слова — следующий ввод
            // будет проверяться против свежего набора (main + alt).
            setCurrentData((prev) => {
              if (!prev || !prev.word) return prev;
              return {
                ...prev,
                word: {
                  ...prev.word,
                  main_translation_text: updated.main_translation_text,
                  translations: updated.translations.map(t => ({ translation_text: t.translation_text })),
                },
              };
            });
          }}
        />

        {/* Context example */}
        {(() => {
          const rawExamples = currentData.context_examples?.length
            ? currentData.context_examples
            : (currentData.word.examples || []).map((e: any) => ({
                en: e.example_sentence,
                en_blanked: e.example_sentence,
                ru: e.example_translation,
                ru_blanked: e.example_translation,
              }));
          if (!rawExamples || rawExamples.length === 0) return null;
          return (
            <ExampleBlock
              examples={rawExamples as any}
              wordOriginal={word.original_text}
              wordTranslation={word.main_translation_text}
              direction={direction}
              isAnswered={cardState !== 'input'}
            />
          );
        })()}

        {/* Correct state */}
        {cardState === 'correct' && (
          <div style={{
            padding: 16, background: 'rgba(0, 230, 118, 0.12)', border: '1px solid var(--theme-success)',
            borderRadius: 12, textAlign: 'center', marginBottom: 12,
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--theme-success)' }}>
              Молодец! ✓
            </div>
            {otherTranslations.length > 0 && (
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginTop: 6 }}>
                А ещё <strong style={{ color: 'var(--theme-text)' }}>{word.original_text}</strong> можно перевести как{' '}
                {otherTranslations.map((t, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <strong style={{ color: 'var(--theme-text)' }}>{t}</strong>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI Smart Feedback Card */}
        {cardState === 'feedback' && lastVerdict && (
          <SmartInputFeedback
            userInput={lastSubmittedInput}
            expectedAnswer={correctAnswer}
            otherTranslations={otherTranslations}
            feedbackType={
              isResetToMc
                ? 'reset_to_mc'
                : (isWrongLanguage || (lastVerdict.ai_hint || '').includes('раскладк'))
                  ? 'layout'
                  : isSynonymFeedback
                    ? 'synonym'
                    : ((lastVerdict.ai_hint || '').includes('форма') || (lastVerdict.ai_hint || '').includes('форму') || (lastVerdict.ai_hint || '').includes('время'))
                      ? 'grammar'
                      : isTypoFeedback
                        ? 'typo'
                        : srsOnly
                          ? 'srs_wrong'
                          : 'wrong'
            }
            aiHint={lastVerdict.ai_hint}
            typoCorrection={isTypoFeedback ? correctAnswer : undefined}
            livesLeft={srsOnly ? null : localLives}
            maxLives={3}
            isSrsReview={srsOnly}
          />
        )}

        {/* Input field */}
        {(cardState === 'input' || cardState === 'checking' || (cardState === 'feedback' && !isResetToMc)) && (
          <>
            <div style={{ marginBottom: 12 }}>
              <input
                ref={inputRef}
                type="text"
                inputMode="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder={
                  cardState === 'feedback' && isSynonymFeedback ? `Введи именно: ${correctAnswer}` :
                  cardState === 'feedback' && isTypoFeedback ? 'Исправь опечатку...' :
                  cardState === 'feedback' ? 'Попробуй ещё раз...' :
                  'Введите перевод...'
                }
                disabled={cardState === 'checking'}
                style={{
                  width: '100%', padding: '14px', background: 'var(--theme-bg-elevated)',
                  border: '1px solid var(--theme-border)', borderRadius: 10, color: 'var(--theme-text)',
                  fontSize: 18, outline: 'none', textAlign: 'center', boxSizing: 'border-box',
                  opacity: cardState === 'checking' ? 0.6 : 1,
                }}
              />
            </div>
            <button
              ref={submitBtnRef}
              onClick={handleSubmit}
              disabled={cardState === 'checking'}
              style={{
                width: '100%', padding: '14px',
                background: cardState === 'checking' ? 'var(--theme-border)' : 'var(--theme-primary)',
                color: cardState === 'checking' ? 'var(--theme-text)' : 'var(--theme-primary-text)', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600,
                cursor: cardState === 'checking' ? 'not-allowed' : 'pointer', minHeight: 44,
              }}
            >
              {cardState === 'checking' ? 'Проверяю...' : 'Проверить'}
            </button>
            {/* Невидимый якорь для скролла — отступ 20px под кнопкой */}
            <div ref={scrollAnchorRef} style={{ height: 20 }} />
          </>
        )}
      </div>

      {/* Кнопка "Далее" при autoAdvance=false */}
      {pendingAdvance && (
        <div style={{ position: 'fixed', bottom: 20, left: 16, right: 16, zIndex: 100 }}>
          <button
            onClick={handleManualAdvance}
            style={{
              width: '100%', padding: 16, background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text)', border: 'none', borderRadius: 12,
              fontSize: 17, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            Далее →
          </button>
        </div>
      )}

      {/* Gift Modal */}
      {showGift && <GiftModal lootDrop={lootDrop} onClose={() => { setShowGift(false); setLootDrop(null); }} />}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
